import type { Inventory } from "./types";
import type { StatusId } from "./status";
export type Slot = "weapon" | "armor" | "charm";
export interface Equipment {
  owned: string[];
  slots: Record<Slot, string | null>;
}
interface Gear {
  name: string;
  slot: Slot;
  note: string;
  cost: Partial<Inventory>;
  speed?: number;
  defense?: number;
  power?: number;
  element?: StatusId;
}
export const GEAR: Record<string, Gear> = {
  reedWand: {
    name: "青枝杖",
    slot: "weapon",
    note: "均衡 · 伤害 +8%",
    cost: { wood: 6, stone: 3 },
    power: 0.08,
  },
  emberWand: {
    name: "焰芽杖",
    slot: "weapon",
    note: "燃烧3秒 · 怕冰；材料来自琥珀矿与远征",
    cost: { wood: 10, spiritDew: 3, islandDust: 5 },
    element: "burn",
  },
  frostWand: {
    name: "霜露杖",
    slot: "weapon",
    note: "减速30% · 3秒；控制优先",
    cost: { stone: 10, spiritDew: 3, islandDust: 5 },
    element: "slow",
  },
  mossCoat: {
    name: "苔织轻衣",
    slot: "armor",
    note: "移速 +10% · 减伤5%",
    cost: { wood: 6, fertilizer: 2 },
    speed: 0.1,
    defense: 0.05,
  },
  crystalCoat: {
    name: "晶脉重甲",
    slot: "armor",
    note: "减伤22% · 移速 -10%",
    cost: { stone: 14, islandDust: 5 },
    speed: -0.1,
    defense: 0.22,
  },
  dewCharm: {
    name: "清露护符",
    slot: "charm",
    note: "进食同时净化毒、火、冰与缓速",
    cost: { spiritDew: 3, wood: 4 },
  },
  rootCharm: {
    name: "盘根护符",
    slot: "charm",
    note: "减伤8% · 移速 -3%",
    cost: { stone: 8, wood: 4 },
    defense: 0.08,
    speed: -0.03,
  },
};
export const emptyEquipment = (): Equipment => ({
  owned: [],
  slots: { weapon: null, armor: null, charm: null },
});
export function equipmentStats(eq: Equipment) {
  const gear = Object.values(eq.slots).flatMap((id) =>
    id && Object.hasOwn(GEAR, id) ? [GEAR[id]] : [],
  );
  return {
    speed: Math.max(0.85, 1 + gear.reduce((n, g) => n + (g.speed ?? 0), 0)),
    defense: Math.min(
      0.4,
      gear.reduce((n, g) => n + (g.defense ?? 0), 0),
    ),
    power: 1 + gear.reduce((n, g) => n + (g.power ?? 0), 0),
    element: gear.find((g) => g.element)?.element,
  };
}
export function craftGear(eq: Equipment, bag: Inventory, id: string) {
  const gear = Object.hasOwn(GEAR, id) ? GEAR[id] : undefined;
  if (
    !gear ||
    eq.owned.includes(id) ||
    Object.entries(gear.cost).some(([k, v]) => bag[k as keyof Inventory] < v)
  )
    return false;
  for (const [k, v] of Object.entries(gear.cost))
    bag[k as keyof Inventory] -= v;
  eq.owned.push(id);
  eq.slots[gear.slot] = id;
  return true;
}
export function equipGear(eq: Equipment, id: string) {
  if (!Object.hasOwn(GEAR, id) || !eq.owned.includes(id)) return false;
  eq.slots[GEAR[id].slot] = id;
  return true;
}
export function recycleGear(eq: Equipment, bag: Inventory, id: string) {
  if (!Object.hasOwn(GEAR, id) || !eq.owned.includes(id)) return false;
  for (const [k, v] of Object.entries(GEAR[id].cost))
    if (k !== "spiritDew" && k !== "islandDust")
      bag[k as keyof Inventory] += Math.floor(v * 0.5);
  eq.owned = eq.owned.filter((x) => x !== id);
  for (const slot of Object.keys(eq.slots) as Slot[])
    if (eq.slots[slot] === id) eq.slots[slot] = null;
  return true;
}
export function readEquipment(value: unknown): Equipment {
  const out = emptyEquipment();
  if (!value || typeof value !== "object") return out;
  const v = value as Partial<Equipment>;
  out.owned = Array.isArray(v.owned)
    ? [
        ...new Set(
          v.owned.filter(
            (id) => typeof id === "string" && Object.hasOwn(GEAR, id),
          ),
        ),
      ]
    : [];
  for (const slot of Object.keys(out.slots) as Slot[]) {
    const id = v.slots?.[slot];
    if (
      typeof id === "string" &&
      out.owned.includes(id) &&
      GEAR[id].slot === slot
    )
      out.slots[slot] = id;
  }
  return out;
}
